import { Radio, Select, Button1, TagInput } from "../../../index";
import { Input, DatePicker, Select as AntSelect } from "antd";
import { useEffect, useRef, useState } from "react";
import swiftIcon from "../../../../assets/temp-icons/swift-icon.png";
import fedwireIcon from "../../../../assets/temp-icons/fedwire.png";
import infoIcon from "../../../../assets/icons/info-icon.png";

import RecipientsInformation from "./RecipientsInformation";
import InternationalFundReview from "./InternationalFundReview";
import { beneficiariesApi, transfersApi } from "../../../../api/endpoints";
import { newIdempotencyKey } from "../../../../utils/idempotency";
import FeeBreakdown from "../../../fee-breakdown/FeeBreakdown";
import TransferOtpModal from "../../../modals/TransferOtpModal";
import {
  DESTINATION_COUNTRIES,
  currencyForCountry,
} from "../../../../data/destinationCountries";

// Recipient picks a destination country; the currency is derived from it.
const DEST_COUNTRY_OPTIONS = DESTINATION_COUNTRIES.map((c) => ({
  name: `${c.flag} ${c.name} (${c.currency})`,
  value: c.iso,
}));
const PURPOSES = [
  "Family Support",
  "Salary",
  "Bill Payment",
  "Education",
  "Medical",
  "Business",
  "Freight",
  "Other",
];
const CATEGORIES = ["Family", "Friend", "Vendor", "Employee", "Business"];
const TRANSFER_METHODS = [
  { icon: swiftIcon, name: "SWIFT" },
  { icon: fedwireIcon, name: "Fedwire" },
];

const InternationalFundTransfer = ({ sourceAccountId, sourceAccount }) => {
  const [step, setStep] = useState(0);

  const [transferData, setTransferData] = useState({
    sourceAmount: "",
    destCurrency: "INR",
    destCountry: "IN",
    accountType: "INDIVIDUAL",
    transferMethodIndex: 0,
    feeShare: "Fees are Shared",
    transferDate: null,
    purpose: "",
    referenceNote: "",
    internalRef: "",
    category: "",
  });

  const [beneficiaryData, setBeneficiaryData] = useState({
    swiftCode: "",
    bankName: "",
    iban: "",
    sortCode: "",
    accountName: "",
    bankCountry: "AU",
    fullName: "",
    businessName: "",
    nickname: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    saveAsBeneficiary: true,
    validationError: "",
  });

  const [sourceCurrency, setSourceCurrency] = useState(sourceAccount?.currency || "AUD");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);

  const updateTransfer = (field, value) =>
    setTransferData((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    setSourceCurrency(sourceAccount?.currency || "AUD");
  }, [sourceAccount?.currency]);

  const updateBeneficiary = (field, value) =>
    setBeneficiaryData((prev) => ({
      ...prev,
      [field]: value,
      ...(field !== "validationError" ? { validationError: "" } : {}),
    }));

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      !sourceAccountId ||
      !transferData.sourceAmount ||
      isNaN(Number(transferData.sourceAmount)) ||
      Number(transferData.sourceAmount) <= 0
    ) {
      setQuote(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      transfersApi
        .quote({
          sourceAccountId,
          sourceAmount: Number(transferData.sourceAmount),
          destCurrency: transferData.destCurrency,
          transferType: "INTERNATIONAL",
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [sourceAccountId, transferData.sourceAmount, transferData.destCurrency]);

  // Confirm button opens the emailed-OTP step; the transfer itself is only
  // created after the backend verifies the code in handleCreate(otpCode).
  const handleConfirm = () => {
    setError(null);
    if (!sourceAccountId) {
      setError("Please select a source account first");
      return;
    }
    if (
      !transferData.sourceAmount ||
      Number(transferData.sourceAmount) <= 0
    ) {
      setError("Enter a valid amount");
      return;
    }
    setOtpOpen(true);
  };

  const handleCreate = async ({ otpCode, pinCode } = {}) => {
    setError(null);
    setSubmitting(true);
    try {
      let beneficiaryId;
      if (beneficiaryData.saveAsBeneficiary) {
        const isBusiness = transferData.accountType === "BUSINESS";
        const fullName = isBusiness
          ? beneficiaryData.businessName || beneficiaryData.accountName
          : beneficiaryData.fullName || beneficiaryData.accountName;
        if (!fullName) {
          setError(
            isBusiness ? "Business name required" : "Recipient name required"
          );
          setSubmitting(false);
          return;
        }
        const created = await beneficiariesApi.add({
          accountType: transferData.accountType,
          fullName: !isBusiness ? fullName : undefined,
          businessName: isBusiness ? fullName : undefined,
          nickname: beneficiaryData.nickname || undefined,
          email: beneficiaryData.email || undefined,
          countryCode: beneficiaryData.bankCountry || transferData.destCountry,
          address: beneficiaryData.address || undefined,
          city: beneficiaryData.city || undefined,
          state: beneficiaryData.state || undefined,
          zipcode: beneficiaryData.zipcode || undefined,
          purpose: transferData.purpose || undefined,
          category: transferData.category || undefined,
          transferMethod:
            TRANSFER_METHODS[transferData.transferMethodIndex]?.name,
          paymentType: "INTERNATIONAL",
          payoutMethod: "BANK_ACCOUNT",
          bankAccount: {
            bankName: beneficiaryData.bankName || undefined,
            iban: beneficiaryData.iban || undefined,
            accountNumber: beneficiaryData.iban || undefined,
            swiftCode: beneficiaryData.swiftCode || undefined,
            sortCode: beneficiaryData.sortCode || undefined,
            routingNumber: beneficiaryData.sortCode || undefined,
            currency: transferData.destCurrency,
          },
        });
        beneficiaryId = created.id;
      }

      await transfersApi.create({
        sourceAccountId,
        sourceAmount: Number(transferData.sourceAmount),
        destCurrency: transferData.destCurrency,
        transferType: "INTERNATIONAL",
        beneficiaryId,
        category: transferData.category || undefined,
        referenceNote: transferData.referenceNote || undefined,
        otpCode,
        pinCode,
      }, idempotencyKey);

      setOtpOpen(false);
      setStep(3);
    } catch (e) {
      // OTP/PIN problems stay inside the modal so the user can retry/resend.
      const apiErr = e.response?.data?.error || "";
      if (apiErr.startsWith("OTP_") || apiErr.startsWith("PIN_")) throw e;
      setOtpOpen(false);
      setError(
        e.response?.data?.message ||
          e.response?.data?.error ||
          e.response?.data?.details?.[0]?.message ||
          e.message ||
          "Transfer failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onTransferMethodSelect = (index) =>
    updateTransfer("transferMethodIndex", index);

  if (step === 1) {
    return (
      <RecipientsInformation
        accountType={transferData.accountType}
        beneficiaryData={beneficiaryData}
        updateBeneficiary={updateBeneficiary}
        onClick={setStep}
      />
    );
  }

  if (step === 2) {
    return (
      <>
        <InternationalFundReview
          sourceAccount={sourceAccount}
          transferData={transferData}
          beneficiaryData={beneficiaryData}
          quote={quote}
          submitting={submitting}
          error={error}
          onConfirm={handleConfirm}
          updateTransfer={updateTransfer}
          onClick={setStep}
        />
        <TransferOtpModal
          open={otpOpen}
          onClose={() => setOtpOpen(false)}
          onVerify={handleCreate}
        />
      </>
    );
  }

  if (step === 3) {
    return (
      <div className="col-12 p-5 d-flex flex-column align-items-center gap-3">
        <span className="fs-3 fw-bold" style={{ color: "#1bbb6b" }}>
          ✓ Transfer created
        </span>
        <span className="fs14px text-muted">
          Your international transfer has been queued.
        </span>
        <Button1
          buttonName={"View transactions"}
          className="btn-dark"
          onClick={() => (window.location.href = "/my-transaction")}
        />
      </div>
    );
  }

  return (
    <>
      <div className="col-lg-5 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">International Transfer</span>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 ">
              <div className=" d-flex flex-column gap-2 w-75">
                <label className="fs12px">You send</label>
                <Input
                  className="p-3 w-100"
                  placeholder="0.00"
                  value={transferData.sourceAmount}
                  onChange={(e) =>
                    updateTransfer("sourceAmount", e.target.value)
                  }
                />
              </div>
              <div className="d-flex flex-column gap-2 new-transfer-currency-field">
                <label className="fs12px">Currency</label>
                <AntSelect
                  value={sourceCurrency}
                  disabled
                  options={[{ label: sourceCurrency, value: sourceCurrency }]}
                  className="new-transfer-currency-select"
                  style={{ width: "100%" }}
                  popupClassName="new-transfer-currency-popup"
                />
              </div>
            </div>
            {sourceAccount && (
              <span className="fs12px text-muted">
                Available:{" "}
                {Number(sourceAccount.availableBalance).toLocaleString()}{" "}
                {sourceAccount.currency}
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Transfer to</label>
            <Select
              value={transferData.destCountry}
              options={DEST_COUNTRY_OPTIONS}
              defaultValue="Select country"
              className="p-3"
              onChange={(e) => {
                const iso = e.target.value;
                setTransferData((prev) => ({
                  ...prev,
                  destCountry: iso,
                  destCurrency: currencyForCountry(iso) || prev.destCurrency,
                }));
              }}
            />
          </div>

          <div className=" d-flex flex-column gap-2">
            <span className="fs12px">Recipients Type</span>
            <div className="d-flex justify-content-between  ">
              <div className="d-flex gap-2 align-items-center w-50">
                <Radio
                  name={"intl-account-type"}
                  accentColor={"green"}
                  height={"20px"}
                  width={"20px"}
                  checked={transferData.accountType === "BUSINESS"}
                  onClick={() => updateTransfer("accountType", "BUSINESS")}
                />
                <span className="fs14px">Business</span>
              </div>
              <div className="d-flex gap-2 align-items-center w-50 justify-content-end justify-content-md-start">
                <Radio
                  name={"intl-account-type"}
                  accentColor={"green"}
                  height={"20px"}
                  width={"20px"}
                  checked={transferData.accountType === "INDIVIDUAL"}
                  onClick={() => updateTransfer("accountType", "INDIVIDUAL")}
                />
                <span className="fs14px">Individual</span>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Transfer method
            </span>
            <div className="d-flex flex-wrap gap-2">
              {TRANSFER_METHODS.map(({ name, icon }, key) => (
                <div
                  key={key}
                  className={`d-flex justify-content-between transfer-method-card  rounded-3 ${
                    key === transferData.transferMethodIndex
                      ? "selected-card"
                      : "border"
                  }  p-3`}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <img src={icon} alt="icon" width={25} />
                    <span className="fs14px fw-medium">{name}</span>
                  </div>
                  <Radio
                    height={"20px"}
                    width={"24px"}
                    accentColor={"green"}
                    name={"intl-transfer-method"}
                    checked={key === transferData.transferMethodIndex}
                    onClick={() => onTransferMethodSelect(key)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            <Select
              value={transferData.feeShare}
              options={[
                { name: "Fees are Shared", value: "Fees are Shared" },
                { name: "Sender pays all", value: "Sender pays all" },
                { name: "Recipient pays all", value: "Recipient pays all" },
              ]}
              defaultValue="Fees are Shared"
              className="p-3"
              onChange={(e) => updateTransfer("feeShare", e.target.value)}
            />
            <div className="d-flex gap-2 align-items-center">
              <img src={infoIcon} alt="info" className="align-self-start" />
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                You pay the outgoing transfer fees and the recipients pays
                the and receiving bank fees
              </span>
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 ">
              <div className="d-flex flex-column gap-2 w-75">
                <label className="fs12px">Recipients get</label>
                <Input
                  className="p-3 w-100"
                  placeholder="0.00"
                  value={quote?.destAmount || ""}
                  readOnly
                />
              </div>
              <div className="d-flex flex-column gap-2 new-transfer-currency-field">
                <label className="fs12px">Currency</label>
                <AntSelect
                  value={transferData.destCurrency}
                  disabled
                  options={[{ label: transferData.destCurrency, value: transferData.destCurrency }]}
                  className="new-transfer-currency-select"
                  style={{ width: "100%" }}
                  popupClassName="new-transfer-currency-popup"
                />
              </div>
            </div>
            {quote && <FeeBreakdown quote={quote} kind="transfer" className="mt-2" />}
          </div>
        </div>
      </div>

      <div className="col-lg-6 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium"> Transfer Details</span>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Transfer date</label>
            <DatePicker
              placeholder={"Select date"}
              className="p-3"
              onChange={(d) =>
                updateTransfer("transferDate", d?.toISOString() || null)
              }
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Swift Transfer usually arrive within 0-3 business days of this
              date
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Purpose</label>
            <Select
              value={transferData.purpose}
              options={PURPOSES.map((p) => ({ name: p, value: p }))}
              defaultValue="Select purpose"
              className="p-3"
              onChange={(e) => updateTransfer("purpose", e.target.value)}
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Please Specify purpose to ensure timely dispatch of transfer
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Transfer reference</label>
            <Input
              placeholder="Payment for invoice #983263"
              className="p-3"
              value={transferData.referenceNote}
              onChange={(e) =>
                updateTransfer("referenceNote", e.target.value)
              }
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Displayed on recipient's bank statement
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">
              Internal reference{" "}
              <span style={{ color: "#7D7D7D" }}>(Optional)</span>
            </label>
            <Input
              placeholder="Job no #293736"
              className="p-3"
              value={transferData.internalRef}
              onChange={(e) =>
                updateTransfer("internalRef", e.target.value)
              }
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              We won't send this to recipient. It needs to be district over
              the last 7 days
            </span>
          </div>

          <div className="d-flex flex-column gap-2 ">
            <label className="fs12px ">Category</label>
            <Select
              value={transferData.category}
              options={CATEGORIES.map((c) => ({ name: c, value: c }))}
              defaultValue="Choose Category"
              className="rounded-2 p-3"
              onChange={(e) => updateTransfer("category", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2 ">
            <label className="fs12px ">Tags</label>
            <TagInput
              className="border py-3 "
              inputBgColor="transparent"
              tagColor="#1bbb6b"
              tagTextColor="#000"
              limit={10}
              placeholder="Add up to 10 Tags"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="col-12 p-2">
          <div className="fs12px" style={{ color: "#FC555B" }}>
            {error}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-md-end justify-content-between p-2 gap-3">
        <Button1
          buttonName={"Save as Draft"}
          className={"btn-outline-success"}
        />

        <Button1
          buttonName={"Continue"}
          className="btn-dark "
          onClick={() => setStep(1)}
        />
      </div>
    </>
  );
};

export default InternationalFundTransfer;

